import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Science as ExperimentIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  Stop as StoppedIcon,
} from '@mui/icons-material';
import {
  getStatusIcon,
  getStatusColor,
  formatDate,
  formatTrafficAllocation,
} from './utils';

const ExperimentCard = ({ experiment }) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Experiment Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ExperimentIcon color="primary" />
            <Typography variant="h6">{experiment.name}</Typography>
          </Box>
          <Chip
            icon={getStatusIcon(experiment.status)}
            label={experiment.status?.toUpperCase() || 'UNKNOWN'}
            color={getStatusColor(experiment.status)}
            size="small"
          />
        </Box>

        {experiment.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {experiment.description}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Experiment Details */}
        <Grid container spacing={2}>
          {/* Basic Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">
              Experiment ID
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 2 }}>
              {experiment.experiment_id || experiment.id}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Traffic Allocation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {formatTrafficAllocation(
                experiment.traffic_allocation,
                experiment.treatments
              )}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Statistical Significance
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {experiment.statistical_significance
                ? `${(experiment.statistical_significance * 100).toFixed(0)}%`
                : 'N/A'}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Minimum Sample Size
            </Typography>
            <Typography variant="body2">
              {experiment.minimum_sample_size || 'N/A'}
            </Typography>
          </Grid>

          {/* Dates */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TimelineIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(experiment.created_at)}
                </Typography>
              </Box>
            </Box>

            {experiment.start_date && (
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUpIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(experiment.start_date)}
                  </Typography>
                </Box>
              </Box>
            )}

            {experiment.end_date && (
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <StoppedIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Ended
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(experiment.end_date)}
                  </Typography>
                </Box>
              </Box>
            )}

            {experiment.updated_at && (
              <Box display="flex" alignItems="center" gap={1}>
                <SettingsIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(experiment.updated_at)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Treatments */}
        {experiment.treatments && experiment.treatments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Treatments ({experiment.treatments.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Template Version
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {experiment.treatments.map((treatment, treatmentIndex) => (
                    <TableRow
                      key={
                        treatment.treatment_id || treatment.id || treatmentIndex
                      }
                      sx={{
                        borderLeft: 3,
                        borderColor:
                          treatment.treatment_type === 'control'
                            ? 'primary.main'
                            : 'secondary.main',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={
                            treatment.treatment_type?.toUpperCase() ||
                            'TREATMENT'
                          }
                          size="small"
                          color={
                            treatment.treatment_type === 'control'
                              ? 'primary'
                              : 'secondary'
                          }
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {treatment.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {treatment.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {treatment.template_version || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Success Metrics */}
        {experiment.success_metrics &&
          experiment.success_metrics.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Success Metrics
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {experiment.success_metrics.map((metric, metricIndex) => (
                  <Tooltip
                    key={metricIndex}
                    title={metric}
                    arrow
                    enterDelay={300}
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '1.25rem',
                        },
                      },
                    }}
                  >
                    <Chip
                      label={metric}
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: '200px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </>
          )}

        {/* Metadata */}
        {experiment.metadata && Object.keys(experiment.metadata).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Additional Information
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(experiment.metadata, null, 2)}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExperimentCard;
